import { FC, useCallback, useMemo } from 'react';
import {
  Button,
  InlineNotification,
  OverflowMenu,
  OverflowMenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Location, LocationFilled } from '@carbon/react/icons';
import type { ColumnFiltersState, Row } from '@tanstack/react-table';

import { FormattedCount } from 'components/formatted-count';
import { InterestSourcePill } from 'components/interest-source-pill';
import { Link } from 'components/link';
import TooltipDecimal from 'components/tooltip-decimal/tooltip-decimal';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { tagsTextColumn } from 'lib/asset-aliases';
import type {
  DataTableConfig,
  DataTableSelectionState,
  ExportCallback,
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import RollupVerificationStatus from 'components/rollup-verification-status/rollup-verification-status';
import { VerificationControls } from 'components/verification-controls/verification-controls';
import { format2, format8, formatRange } from 'utils/number';
import {
  subtractCoverageLabel,
  subtractCoverageSuffix,
} from 'utils/subtract-coverage';

import {
  useHydrateVerifications,
  useNriRollup,
  useNriVerification,
} from './hooks';
import { PropertyIdLink, UnitLink } from './property-links';
import type {
  FlatSlotRow,
  InterestBreakdown,
  InterestBreakdownVerification,
  SlotSelectionState,
  VerificationType,
} from './types';
import {
  canEditRow,
  getSubtractVerification,
  WELL_STATUS_OPTIONS,
} from './utils';
import styles from './slot-table.module.scss';

/**
 * Parent-row rollup cell. Reads the row's subtracts via the feature-local
 * rollup hook (which falls back to BE state per subtract for entries the
 * user hasn't touched), and pairs the status icon with the tooltip-
 * formatted slot rollup value.
 *
 * @param {Object} props - Component props
 * @param {InterestBreakdown[] | null} props.subtracts - Row's per-subtract entries
 * @param {VerificationType} props.type - 'nri' or 'wi'
 * @param {number | null} props.value - Slot rollup value to render
 * @return {JSX.Element} The decorated cell
 */
const NriRollupCell: FC<{
  subtracts: InterestBreakdown[] | null;
  type: VerificationType;
  value: number | null;
}> = ({ subtracts, type, value }) => {
  const counts = useNriRollup(subtracts, type);
  return (
    <div className={styles.nriValueCell}>
      {counts.totalSteps > 0 && <RollupVerificationStatus {...counts} />}
      <TooltipDecimal value={value} precision={8} />
    </div>
  );
};

/** Props for {@link SlotInterestCell}. */
interface SlotInterestCellProps {
  /** The row whose property NRI or WI the cell shows. */
  row: FlatSlotRow;
  /** 'nri' or 'wi' */
  type: VerificationType;
  /** The property's NRI or WI. */
  value: number | null;
}

/**
 * A Total Property NRI or WI cell: a certified value with its source pill,
 * else the value with its subtracts' verification rollup and the pill.
 *
 * @param {SlotInterestCellProps} props - Component props
 * @return {JSX.Element} The cell
 */
function SlotInterestCell({ row, type, value }: SlotInterestCellProps) {
  if (row.wiSource === 'certified') {
    return (
      <div className={styles.nriValueCell}>
        <TooltipDecimal value={value} precision={8} />
        <InterestSourcePill wiSource={row.wiSource} />
      </div>
    );
  }
  return (
    <span className={styles.interestCell}>
      <NriRollupCell subtracts={row.subtracts} type={type} value={value} />
      <InterestSourcePill wiSource={row.wiSource} />
    </span>
  );
}

// BE-filterable visible columns get a text or enum filter UI; everything
// else is gated with `enableColumnFilter: false` so the inline filter row
// renders empty cells. `slotNumber` is BE-filterable but its 'integer'
// meta derives a range filter, which doesn't match the BE's `IN (...)`
// array semantics — left disabled until a single-int filter UI lands.
// Sortable iff the BE's `Query::Packages::DraftSlots::SORTABLE_ATTRIBUTES`
// allowlist exposes the column. An out-of-allowlist sort_attribute is
// rejected by the BE (the inclusion validation, a 422), so we mark
// non-sortable columns `enableSorting: false` so DataTable doesn't render
// a sort indicator the user can click.
const STATIC_COLUMNS: DataTableColumnDef<FlatSlotRow>[] = [
  {
    id: 'slotId',
    accessorKey: 'slotId',
    header: 'Property ID',
    meta: { type: 'text' as const },
    cell: ({ row, getValue }) => (
      <PropertyIdLink
        propertyId={getValue<string>()}
        uwi={row.original.uwi}
        unitHref={row.original.unitHref}
      />
    ),
  },
  {
    id: 'assetName',
    accessorKey: 'assetName',
    header: 'Asset',
    meta: { type: 'text' as const },
    cell: ({ row }) => {
      const {
        assetType,
        assetId,
        assetName,
        includedSubtractCount,
        subtractCount,
      } = row.original;
      if (assetType !== 'Tract') return assetId;
      const display = assetName ?? '—';
      // For tract rows, `assetId` IS the salesforce id — link to the
      // full-page CRM Tract Wizard edit route in a new tab. A Tract held in
      // part says how much of it the draft holds.
      if (!assetId) return display;
      const label = subtractCoverageLabel(includedSubtractCount, subtractCount);
      return (
        <>
          <Link
            to={`/crm/tracts_and_subtracts/${encodeURIComponent(assetId)}/edit`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {display}
          </Link>
          {subtractCoverageSuffix(label)}
        </>
      );
    },
  },
  // `accessorFn` derives the displayed label from the wire value: BE/jbuilder
  // emits 'LTD' on the wire (see DraftTractAssetType), but the AC + product
  // copy displays 'LTD Slot'. The BE wire stays narrow ('LTD'); the FE
  // expands at render time so a future column-filter on the wire shape can
  // still match the canonical three-value enum.
  {
    id: 'assetType',
    accessorFn: (row: FlatSlotRow) => {
      return row.assetType === 'LTD' ? 'LTD Slot' : row.assetType;
    },
    header: 'Asset Type',
    enableColumnFilter: false,
  },
  // The tract's tags as comma text, blank with none (INS-13689); the search box finds a row by one.
  tagsTextColumn<FlatSlotRow>({
    id: 'crmTractTags',
    accessorKey: 'crmTractTags',
    header: 'Tract Tags',
    enableColumnFilter: false,
  }),
  {
    id: 'crmAccountName',
    accessorKey: 'crmAccountName',
    header: 'CRM Account',
    enableColumnFilter: false,
  },
  {
    id: 'unitName',
    accessorKey: 'unitName',
    header: 'Unit',
    meta: { type: 'text' as const },
    cell: ({ row, getValue }) => (
      <UnitLink
        unitName={getValue<string | null>()}
        unitHref={row.original.unitHref}
      />
    ),
  },
  // The unit's tags as comma text, blank with none (INS-13689).
  tagsTextColumn<FlatSlotRow>({
    id: 'unitTags',
    accessorKey: 'unitTags',
    header: 'Unit Tags',
    enableColumnFilter: false,
  }),
  {
    id: 'slotNri',
    accessorKey: 'slotNri',
    header: 'Total Property NRI',
    size: 300,
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row, getValue }) => (
      <SlotInterestCell
        row={row.original}
        type="nri"
        value={getValue<number | null>()}
      />
    ),
  },
  {
    id: 'slotWorkingInterest',
    accessorKey: 'slotWorkingInterest',
    header: 'Total Property WI',
    size: 300,
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row, getValue }) => (
      <SlotInterestCell
        row={row.original}
        type="wi"
        value={getValue<number | null>()}
      />
    ),
  },
  {
    id: 'allocationMethod',
    accessorKey: 'allocationMethod',
    header: 'Allocation Method',
    enableColumnFilter: false,
  },
  {
    id: 'tractNetAcres',
    accessorKey: 'tractNetAcres',
    header: 'Tract Net Acres',
    meta: { type: 'decimal' as const },
    enableColumnFilter: false,
  },
  {
    id: 'netAcreageUnits',
    accessorKey: 'netAcreageUnits',
    header: 'Net Acreage Units',
    enableColumnFilter: false,
  },
  {
    id: 'tractGrossAcres',
    accessorKey: 'tractGrossAcres',
    header: 'Tract Gross Acres',
    meta: { type: 'decimal' as const },
    enableColumnFilter: false,
  },
  {
    id: 'surveyArea',
    accessorKey: 'surveyArea',
    header: 'Survey Area',
    enableColumnFilter: false,
  },
  {
    id: 'tractQuarterCall',
    accessorKey: 'tractQuarterCall',
    header: 'Quarter Call',
    enableColumnFilter: false,
  },
  {
    id: 'instrumentType',
    accessorKey: 'instrumentType',
    header: 'Interest Type',
    enableColumnFilter: false,
  },
  {
    id: 'interestType',
    accessorKey: 'interestType',
    header: 'Ownership Type',
    enableColumnFilter: false,
  },
  {
    id: 'depthRange',
    header: 'Depth Range',
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) =>
      formatRange({
        start: row.original.startDepth,
        end: row.original.endDepth,
      }),
  },
  {
    id: 'wellName',
    accessorKey: 'wellName',
    header: 'Well Name',
    enableColumnFilter: false,
  },
  {
    id: 'wellStatus',
    accessorKey: 'wellStatus',
    header: 'Well Status',
    meta: { type: 'enum' as const, filterOptions: WELL_STATUS_OPTIONS },
  },
  {
    id: 'operator',
    accessorKey: 'operator',
    header: 'Operator',
    meta: { type: 'text' as const },
  },
  {
    id: 'holeDirection',
    accessorKey: 'holeDirection',
    header: 'Hole Direction',
    enableColumnFilter: false,
  },
  {
    id: 'formation',
    accessorKey: 'formation',
    header: 'Formation',
    meta: { type: 'text' as const },
  },
  {
    id: 'slotNumber',
    accessorKey: 'slotNumber',
    header: 'Property #',
    meta: { type: 'integer' as const },
    enableColumnFilter: false,
  },
  {
    id: 'wellPermitDate',
    accessorKey: 'wellPermitDate',
    header: 'Permit Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'wellFirstProductionDate',
    accessorKey: 'wellFirstProductionDate',
    header: 'First Prod. Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
];

const getRowId = (row: FlatSlotRow): string => row.rowId;

/**
 * Synthetic id placed in `toggledNodes` when BE is 'mixed' but the
 * current page has no selected rows — keeps DataTable's header in
 * the indeterminate state (its rule fires when `toggledNodes.size > 0`).
 * Real `rowId`s are `${slotId}__${assetId}` from `flattenDraftSlots`,
 * so the double-underscore-bracketed prefix can't collide.
 */
const MIXED_SENTINEL = '__be_mixed_marker__';

const BASE_CONFIG: DataTableConfig = {
  globalSearch: true,
  expandableRows: true,
  sorting: true,
};

const SELECTABLE_CONFIG: DataTableConfig = {
  ...BASE_CONFIG,
  selectionType: 'multi',
};

type SubtractDisplayKey = Exclude<
  keyof InterestBreakdown,
  'verifications' | 'bundleOfSticks'
>;

const formatText = (v: string | number | null): string => {
  if (v === null || v === undefined || v === '') return '--';
  return String(v);
};

const SUBTRACT_HEADERS: Array<{
  key: SubtractDisplayKey;
  header: string;
  format: (value: string | number | null) => string;
}> = [
  { key: 'subtractName', header: 'CRM Subtract', format: formatText },
  { key: 'uatName', header: 'UAT', format: formatText },
  { key: 'nri', header: 'UAT NRI', format: format8 },
  { key: 'workingInterest', header: 'UAT WI', format: format8 },
  { key: 'netAcres', header: 'Subtract Net Acres', format: format2 },
  { key: 'grossAcres', header: 'Subtract Gross Acres', format: format2 },
  { key: 'quarterCall', header: 'Quarter Call', format: formatText },
  {
    key: 'participationTractFactor',
    header: 'Part. Tract Factor',
    format: format8,
  },
  {
    key: 'participatingTractAcres',
    header: 'Part. Tract Acres',
    format: format2,
  },
  { key: 'ptfAdjustment', header: 'PTF Adjustment', format: format8 },
  { key: 'unitCoverage', header: 'Unit Coverage', format: format8 },
  { key: 'uatCoverage', header: 'UAT Coverage', format: format8 },
  { key: 'allocationMethod', header: 'Allocation Method', format: formatText },
  { key: 'interestType', header: 'Ownership Type', format: formatText },
];

/**
 * UAT NRI / UAT WI cell in the sub-table — value + verification controls.
 * Renders just the value when `nriCalculationId` is null (no stable key
 * to persist verification state under); otherwise mounts an inner cell
 * that bridges the feature-local `useNriVerification` hook to the lib
 * `<VerificationControls>` primitive.
 *
 * @param {Object} props - Component props
 * @param {InterestBreakdown} props.entry - Breakdown row
 * @param {VerificationType} props.type - Field selector
 * @return {JSX.Element} The decorated cell
 */
const NriCell: FC<{ entry: InterestBreakdown; type: VerificationType }> = ({
  entry,
  type,
}) => {
  const value = type === 'nri' ? entry.nri : entry.workingInterest;
  const { nriCalculationId } = entry;
  if (nriCalculationId === null) {
    return <TooltipDecimal value={value} precision={8} />;
  }
  return (
    <NriCellWithVerification
      nriCalculationId={nriCalculationId}
      verification={getSubtractVerification(entry, type)}
      type={type}
      value={value}
    />
  );
};

/**
 * Inner cell — mounted only when `nriCalculationId` is non-null so the
 * `useNriVerification` hook stays unconditional.
 *
 * @param {Object} props - Component props
 * @param {number} props.nriCalculationId - Calculation id (non-null guaranteed by parent)
 * @param {InterestBreakdownVerification} props.verification - BE-supplied verification record
 * @param {VerificationType} props.type - 'nri' or 'wi'
 * @param {number | null} props.value - Numeric value to render
 * @return {JSX.Element} The decorated cell
 */
const NriCellWithVerification: FC<{
  nriCalculationId: number;
  verification: InterestBreakdownVerification;
  type: VerificationType;
  value: number | null;
}> = ({ nriCalculationId, verification, type, value }) => {
  const verif = useNriVerification(nriCalculationId, type);
  return (
    <div className={styles.nriValueCell}>
      <TooltipDecimal value={value} precision={8} />
      <VerificationControls
        initialStatus={verif.status}
        initialCommentsCount={verif.commentsCount}
        onStatusChange={verif.onStatusChange}
        onCommentsCountChange={verif.onCommentsCountChange}
        eventsHref={verification.href.events ?? ''}
        commentsHref={verification.href.comments ?? ''}
        disableActions={value === null}
        skipModal
      />
    </div>
  );
};

/**
 * Sub-table rendered inside an expanded slot row. One row per subtract
 * entry from the parent tract; renders the 13 direct fields (skipping
 * `bundleOfSticks` decomposition for v1). Empty-state row when the tract
 * has no subtracts.
 *
 * @param {Object} props - Component props
 * @param {InterestBreakdown[] | null} props.subtracts - Subtract rows or null
 * @return {JSX.Element} The sub-table
 */
const SubtractSubTable: FC<{ subtracts: InterestBreakdown[] | null }> = ({
  subtracts,
}) => {
  if (!subtracts || subtracts.length === 0) {
    return (
      <div className={styles.subtableEmpty}>
        No subtract breakdown available
      </div>
    );
  }
  return (
    <div className={styles.subtableWrap}>
      <Table size="sm" className={styles.subtable}>
        <TableHead>
          <TableRow>
            {SUBTRACT_HEADERS.map((h) => (
              <TableHeader key={String(h.key)} id={`subtract-${String(h.key)}`}>
                {h.header}
              </TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {subtracts.map((entry) => (
            <TableRow key={entry.subtractId}>
              {SUBTRACT_HEADERS.map((h) => {
                if (h.key === 'nri') {
                  return (
                    <TableCell key={String(h.key)}>
                      <NriCell entry={entry} type="nri" />
                    </TableCell>
                  );
                }
                if (h.key === 'workingInterest') {
                  return (
                    <TableCell key={String(h.key)}>
                      <NriCell entry={entry} type="wi" />
                    </TableCell>
                  );
                }
                if (h.key === 'subtractName') {
                  // `subtractId` is the salesforce id (see slot_materializer:646).
                  // Link to the full-page CRM Tract Wizard in a new tab when
                  // both an id and a name are present.
                  const display = h.format(entry.subtractName);
                  if (!entry.subtractId || !entry.subtractName) {
                    return <TableCell key={String(h.key)}>{display}</TableCell>;
                  }
                  return (
                    <TableCell key={String(h.key)}>
                      <Link
                        to={`/crm/tracts_and_subtracts/${encodeURIComponent(
                          entry.subtractId
                        )}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {display}
                      </Link>
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={String(h.key)}>
                    {h.format(entry[h.key])}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/** Props for the {@link SlotTable} component */
export interface SlotTableProps {
  /**
   * Currently-pinned slot id, drives the pin column's active state.
   * Multiple rows can share a `slotId` (one per tract within the slot);
   * all of them light up when their slot is the pinned one.
   */
  pinnedSlotId?: string | null;
  /**
   * Pin click handler. Receives the row's slot id; the wrapper writes it
   * (or null, when the user clicks an already-pinned row) into
   * `focusedSlotIdAtom` for the wizard map to consume.
   */
  onPinToggle?: (slotId: string) => void;
  /**
   * Pre-flattened slot rows — one per `(slot, tract)` pair. Caller produces
   * via `flattenDraftSlots(response.slots)`. Keeps this component dumb so
   * tests don't need nested fixtures and a future consumer can pre-filter
   * the flat rows.
   */
  rows: FlatSlotRow[];
  /** Server-paged total slot-unit count (from `response.meta.totalCount`). */
  totalCount: number | undefined;
  /** Whether a fetch is in flight. */
  isLoading: boolean;
  /**
   * Page / pageSize / globalFilter change handler. Wire into the parent's
   * search-payload setter — the same shape `searchAtomPair` consumes.
   */
  loadData: (params: TableState) => void;
  /**
   * Filter state change handler. Plumbed for when a filter panel lands;
   * unused while the table has no `renderFilterPanel`. Caller translates
   * into the BE's array-filter keys via `draftSlotFilterStateToParams`.
   */
  onFilterChange?: (filters: ColumnFiltersState) => void;
  /**
   * Initial pagination / search / column-visibility seed. `selection` is
   * intentionally excluded — SlotTable owns the BE-driven mapping and
   * silently overriding it here would desync the visible UI from server
   * truth.
   */
  initialTableState?: Omit<InitialTableState, 'selection'>;
  /**
   * Cross-page selection state from the BE response's
   * `meta.selectionState`. Drives the header tristate via the canonical
   * mapping: 'all' → `{selectAll: true, []}`, 'none' → `{false, []}`,
   * 'mixed' → `{false, [selected ids]}`. Omit (with `onToggleRow` /
   * `onToggleAll`) to render the table without checkboxes.
   */
  selectionState?: SlotSelectionState;
  /**
   * Single-row toggle intent. Fires when the user clicks an individual
   * row checkbox; `selected` is the row's new selected state. The
   * wrapper translates into a BE `{ allocations, selected }` PATCH.
   */
  onToggleRow?: (row: FlatSlotRow, selected: boolean) => void;
  /**
   * Header-click intent. Fires when the user clicks the header
   * checkbox; `selected` is the new universal state (true → select-all,
   * false → deselect-all). Wrapper sends `{ selectAll, filters? }`.
   */
  onToggleAll?: (selected: boolean) => void;
  /**
   * Disable selection interaction (checkboxes go disabled) while a
   * mutation is in flight. Existing checkbox visuals stay; only
   * interaction is gated.
   */
  disableSelection?: boolean;
  /**
   * When set, renders a Carbon `<InlineNotification>` above the table.
   * Wrappers that already render their own banner outside `<SlotTable>`
   * (e.g. Step 1) leave this undefined.
   */
  errorMessage?: string | null;
  /**
   * When set, renders a "Properties (N)" header above the table — mirrors
   * the asset table's pattern. The numeric value is rendered through
   * `<FormattedCount>` for locale formatting.
   */
  headerCount?: number;
  /** When true, the header reads "Properties (N selected)" instead of "Properties (N)". */
  showSelectedSuffix?: boolean;
  /**
   * Server-side export callback. When set, the export button POSTs the
   * filter/search scope to the BE and downloads the CSV stream rather
   * than building one client-side from the in-view rows.
   */
  onExport?: ExportCallback;
  /**
   * Per-row Edit handler (INS-9066). Fires when the user picks "Edit"
   * from the row's overflow menu — only emitted for non-Tract rows.
   * Tract rows get their Edit menu suppressed automatically because per
   * INS-9066: "Tract rows are not editable here" — they round-trip
   * through the existing CRM Tract Creation Wizard, not this overflow
   * menu. Receives the row so the parent can route to the appropriate
   * modal (Wellbore vs LTD) and pre-populate it. Omit (with
   * `onRemoveAsset`) to drop the actions column entirely.
   */
  onEditAsset?: (row: FlatSlotRow) => void;
  /**
   * Per-row Remove handler (INS-9066). Fires when the user picks
   * "Remove" from the row's overflow menu. Wrapper translates into a
   * PATCH `/packages/drafts/:id/assets` with
   * `removed_asset_ids: [row.assetId]`.
   */
  onRemoveAsset?: (row: FlatSlotRow) => void;
  /** Disables the per-row Remove, e.g. while the draft is being rebuilt. */
  disableRemove?: boolean;
}

/**
 * View-only slot table backed by pre-flattened `(slot, tract)` rows.
 * Mirrors the v5 mock's `SlotsTable`: 23 visible columns at the slot/tract
 * level, expanding into a Carbon sub-table of subtract rows.
 * Selection / focus / map / NRI calculation icons / filter panel are
 * intentionally out of scope for v1 — caller passes data, this component
 * renders.
 *
 * @param {SlotTableProps} props - Rows, totalCount, loading, and handlers
 * @return {JSX.Element} The slot table
 */
export const SlotTable: FC<SlotTableProps> = ({
  rows,
  totalCount,
  isLoading,
  loadData,
  onFilterChange,
  initialTableState,
  selectionState,
  onToggleRow,
  onToggleAll,
  disableSelection,
  pinnedSlotId,
  onPinToggle,
  onEditAsset,
  onRemoveAsset,
  disableRemove = false,
  errorMessage,
  headerCount,
  showSelectedSuffix,
  onExport,
}) => {
  const hasSelection =
    selectionState !== undefined &&
    onToggleRow !== undefined &&
    onToggleAll !== undefined;

  const columns = useMemo<DataTableColumnDef<FlatSlotRow>[]>(() => {
    const cols: DataTableColumnDef<FlatSlotRow>[] = onPinToggle
      ? [
          {
            id: 'pin-action',
            header: '',
            cell: ({ row }) => {
              const isPinned = row.original.slotId === pinnedSlotId;
              return (
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={isPinned ? LocationFilled : Location}
                  iconDescription={isPinned ? 'Unfocus on map' : 'Focus on map'}
                  tooltipPosition="right"
                  onClick={() => onPinToggle(row.original.slotId)}
                />
              );
            },
          },
          ...STATIC_COLUMNS,
        ]
      : [...STATIC_COLUMNS];

    // INS-9066: per-row Edit + Remove via Carbon OverflowMenu. Append a
    // trailing actions column ONLY when the wrapper supplies at least one
    // handler — otherwise we don't want an empty kebab cluttering the row.
    // Edit is suppressed for tract rows: per INS-9066 "Tract rows are not
    // editable here" — they round-trip through the existing CRM Tract
    // wizard, not this menu. If the only menu item that would fire is
    // Edit-on-a-Tract, skip the menu entirely so the row stays clean.
    if (onEditAsset || onRemoveAsset) {
      cols.push({
        id: 'row-actions',
        header: '',
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const canEdit =
            onEditAsset !== undefined && canEditRow(row.original.assetType);
          const canRemove = onRemoveAsset !== undefined;
          if (!canEdit && !canRemove) return null;
          return (
            <OverflowMenu
              size="sm"
              flipped
              aria-label={`Actions for ${
                row.original.assetName ?? row.original.assetId
              }`}
            >
              {canEdit && (
                <OverflowMenuItem
                  itemText="Edit"
                  onClick={() => onEditAsset?.(row.original)}
                />
              )}
              {canRemove && (
                <OverflowMenuItem
                  itemText="Remove"
                  isDelete
                  disabled={disableRemove}
                  onClick={() => onRemoveAsset?.(row.original)}
                />
              )}
            </OverflowMenu>
          );
        },
      });
    }

    return cols;
  }, [pinnedSlotId, onPinToggle, onEditAsset, onRemoveAsset, disableRemove]);
  // Seed feature-local verification map with BE state on each fetch.
  // Cells/rollup hooks read this map; without hydrate they'd see empty
  // state until the user interacts.
  useHydrateVerifications(rows);

  const renderRowSubComponent = useCallback(
    (row: Row<FlatSlotRow>) => (
      <SubtractSubTable subtracts={row.original.subtracts} />
    ),
    []
  );

  const getRowCanExpand = useCallback(
    (row: Row<FlatSlotRow>) => row.original.assetType === 'Tract',
    []
  );

  const scroll = useMemo(
    () => ({
      mode: 'paginated' as const,
      serverDriven: true as const,
      totalCount: totalCount ?? 0,
    }),
    [totalCount]
  );

  // Canonical mapping from BE truth → DataTable's `{selectAll, toggledNodes}`.
  // 'all' uses the implicit form (no enumeration) so the header renders
  // checked. 'none' is empty. 'mixed' enumerates the visible-and-selected
  // rowIds; DataTable then renders the header indeterminate (its rule:
  // any non-empty toggledNodes → indeterminate, regardless of selectAll).
  //
  // Edge: if BE is 'mixed' but no visible rows are selected (e.g. user
  // is on a page where all selections live on other pages), we'd emit
  // `{false, []}` — visually identical to 'none' and losing the mixed
  // signal. Inject a sentinel id so toggledNodes stays non-empty. The
  // sentinel doesn't match any real row, so cells render correctly
  // (unchecked) and `findRow` in the diff handler ignores it.
  //
  // When selection isn't enabled, returns the empty state — `tableState`
  // below ignores it and the DataTable skips the selection column entirely.
  const selection = useMemo<DataTableSelectionState>(() => {
    if (selectionState === 'all') {
      return { selectAll: true, toggledNodes: [] };
    }
    if (selectionState === 'none' || selectionState === undefined) {
      return { selectAll: false, toggledNodes: [] };
    }
    const selectedIds = rows.filter((r) => r.selected).map(getRowId);
    if (selectedIds.length > 0) {
      return { selectAll: false, toggledNodes: selectedIds };
    }
    return { selectAll: false, toggledNodes: [MIXED_SENTINEL] };
  }, [selectionState, rows]);

  // `selection` (BE truth) is fed as CONTROLLED state (below); the caller's
  // `initialTableState` carries only the non-selection seed.

  // A single slot fans out into multiple rows (one per tract), so all of
  // them light up together when their slot is the pin target.
  const highlightedRowIds = useMemo<Set<string>>(() => {
    if (!pinnedSlotId) return new Set();
    return new Set(rows.filter((r) => r.slotId === pinnedSlotId).map(getRowId));
  }, [rows, pinnedSlotId]);

  /**
   * Diff-translate DataTable's selection emit into typed intents.
   *
   * - `selectAll` flipped → header click → `onToggleAll(next.selectAll)`.
   *   DataTable always clears `toggledNodes` on header click, so any
   *   prior individual selections are intentionally subsumed by the BE's
   *   `select_all` action — the bulk mutation is the new ground truth.
   * - `toggledNodes` changed (selectAll same) → individual row clicks →
   *   one `onToggleRow(row, selected)` per added/removed id. Semantics
   *   flip on `prev.selectAll`: when false, toggledNodes is the
   *   selected set; when true, it's the exception (deselected) set.
   *
   * @param {DataTableSelectionState} next - DataTable's new selection
   * @return {void}
   */
  const handleRowSelect = useCallback(
    (next: DataTableSelectionState) => {
      if (!hasSelection) return;
      if (next.selectAll !== selection.selectAll) {
        onToggleAll(next.selectAll);
        return;
      }
      const prevSet = new Set(selection.toggledNodes);
      const nextSet = new Set(next.toggledNodes);
      const added = next.toggledNodes.filter((id) => !prevSet.has(id));
      const removed = selection.toggledNodes.filter((id) => !nextSet.has(id));
      // When prev.selectAll is true, toggledNodes are exceptions (deselects),
      // so the boolean intent flips.
      const addedSelected = !selection.selectAll;
      const removedSelected = selection.selectAll;
      const rowsById = new Map(rows.map((r) => [r.rowId, r]));
      added.forEach((id) => {
        const row = rowsById.get(id);
        if (row) onToggleRow(row, addedSelected);
      });
      removed.forEach((id) => {
        const row = rowsById.get(id);
        if (row) onToggleRow(row, removedSelected);
      });
    },
    [hasSelection, selection, rows, onToggleRow, onToggleAll]
  );

  return (
    <>
      {headerCount !== undefined && (
        <header className={styles.tableHeader}>
          Properties (<FormattedCount value={headerCount} />
          {showSelectedSuffix ? ' selected' : ''})
        </header>
      )}
      {errorMessage !== null && errorMessage !== undefined && (
        <InlineNotification
          kind="error"
          title="Properties table error"
          subtitle={errorMessage}
          hideCloseButton
          lowContrast
          className={styles.errorNotification}
        />
      )}
      <div className={styles.tableWrap}>
        <DataTable<FlatSlotRow>
          data={rows}
          columns={columns}
          getRowId={getRowId}
          isLoading={isLoading}
          scroll={scroll}
          config={{
            ...(hasSelection ? SELECTABLE_CONFIG : BASE_CONFIG),
            ...(onExport && {
              export: {
                exportPresets: [{ name: 'Export', format: 'csv', onExport }],
              },
            }),
          }}
          initialTableState={initialTableState}
          controlledState={hasSelection ? { selection } : undefined}
          searchPlaceholder="Search by Property ID, Well Name, or tags"
          onPaginationChange={loadData}
          onSortChange={loadData}
          onGlobalSearchChange={loadData}
          onFilterChange={
            onFilterChange ? (s) => onFilterChange(s.columnFilters) : undefined
          }
          {...(hasSelection && {
            onSelectionChange: (s: TableState) => handleRowSelect(s.selection),
            disableSelection,
          })}
          renderRowSubComponent={renderRowSubComponent}
          getRowCanExpand={getRowCanExpand}
          highlightedRowIds={highlightedRowIds}
        />
      </div>
    </>
  );
};
